'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@7f-ui/components/button/button';
import { Flag } from '@7f-ui/components/flag/flag';
import { SimpleModal } from '@7f-ui/components/modal/simple-modal';
import { Skeleton } from '@7f-ui/components/skeleton/skeleton';
import { useGraphQL } from '@data-access/lib/graphql-query';
import { shortenText } from '@utils/helpers';
import { formatGermanNumber, formatMonthYear } from '@utils/utils-format';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { buildPartnerUrl } from 'src/app/partner/_lib/partner-url';
import { PartnerProfileModalDocument } from 'src/lib/backend/documents/partner';
const ABOUT_TEXT_TRUNCATE_LENGTH = 200;
type AppExperiencePartnerProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  publicId: number;
  onViewProfileClick: () => void;
};
export function AppExperiencePartnerProfileModal(t0) {
  const $ = _c(46);
  const {
    isOpen,
    onClose,
    publicId,
    onViewProfileClick
  } = t0;
  let t1;
  if ($[0] !== publicId) {
    t1 = {
      publicId
    };
    $[0] = publicId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isOpen) {
    t2 = {
      enabled: isOpen,
      select: _temp
    };
    $[2] = isOpen;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: profile,
    isLoading,
    isError
  } = useGraphQL(PartnerProfileModalDocument, t1, t2);
  let content;
  if (isLoading) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex flex-col gap-4"><div className="flex items-center gap-4"><Skeleton variant="rounded" className="size-16 shrink-0" /><Skeleton className="h-6 w-40" /></div><Skeleton className="h-24 w-full" /></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    content = t3;
  } else {
    if (isError || !profile) {
      let t3;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <p className="font-body-copy">Das Profil ist derzeit nicht verfügbar.</p>;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      content = t3;
    } else {
      let t3;
      if ($[6] !== profile.profileImage.alt || $[7] !== profile.profileImage.url) {
        t3 = <Image className="size-16 shrink-0 rounded-xl object-cover" src={profile.profileImage.url} alt={profile.profileImage.alt} width={128} height={128} />;
        $[6] = profile.profileImage.alt;
        $[7] = profile.profileImage.url;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      let t4;
      if ($[9] !== profile.name) {
        t4 = <p className="font-body-copy font-semibold">{profile.name}</p>;
        $[9] = profile.name;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      let t5;
      if ($[11] !== profile.city || $[12] !== profile.tagline) {
        t5 = (profile.tagline || profile.city) && <p className="font-body-small text-secondary-text">{[profile.tagline, profile.city].filter(Boolean).join(" \xB7 ")}</p>;
        $[11] = profile.city;
        $[12] = profile.tagline;
        $[13] = t5;
      } else {
        t5 = $[13];
      }
      let t6;
      if ($[14] !== t4 || $[15] !== t5) {
        t6 = <div>{t4}{t5}</div>;
        $[14] = t4;
        $[15] = t5;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      let t7;
      if ($[17] !== t3 || $[18] !== t6) {
        t7 = <div className="flex items-center gap-4">{t3}{t6}</div>;
        $[17] = t3;
        $[18] = t6;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      let t8;
      if ($[20] !== profile.memberSince) {
        t8 = profile.memberSince && <Flag variant="outline">{`Mitglied seit ${formatMonthYear(profile.memberSince)}`}</Flag>;
        $[20] = profile.memberSince;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      const t9 = profile.experienceCount === 1 ? `${formatGermanNumber(profile.experienceCount)} Erlebnis` : `${formatGermanNumber(profile.experienceCount)} Erlebnisse`;
      let t10;
      if ($[22] !== t9) {
        t10 = <Flag variant="outline">{t9}</Flag>;
        $[22] = t9;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      let t11;
      if ($[24] !== t10 || $[25] !== t8) {
        t11 = <div className="flex flex-wrap gap-2">{t8}{t10}</div>;
        $[24] = t10;
        $[25] = t8;
        $[26] = t11;
      } else {
        t11 = $[26];
      }
      let t12;
      if ($[27] !== profile.aboutText) {
        t12 = shortenText(profile.aboutText, ABOUT_TEXT_TRUNCATE_LENGTH, true, true);
        $[27] = profile.aboutText;
        $[28] = t12;
      } else {
        t12 = $[28];
      }
      let t13;
      if ($[29] !== t12) {
        t13 = <p className="font-body-copy">{t12}</p>;
        $[29] = t12;
        $[30] = t13;
      } else {
        t13 = $[30];
      }
      let t14;
      if ($[31] !== profile.publicId || $[32] !== profile.slug) {
        t14 = buildPartnerUrl({
          publicId: profile.publicId,
          slug: profile.slug
        });
        $[31] = profile.publicId;
        $[32] = profile.slug;
        $[33] = t14;
      } else {
        t14 = $[33];
      }
      let t15;
      if ($[34] !== onViewProfileClick || $[35] !== t14) {
        t15 = <Button as={Link} href={t14} target="_blank" variant="primary" onClick={onViewProfileClick}>Profil ansehen</Button>;
        $[34] = onViewProfileClick;
        $[35] = t14;
        $[36] = t15;
      } else {
        t15 = $[36];
      }
      let t16;
      if ($[37] !== t11 || $[38] !== t13 || $[39] !== t15 || $[40] !== t7) {
        t16 = <div className="flex flex-col gap-4">{t7}{t11}{t13}{t15}</div>;
        $[37] = t11;
        $[38] = t13;
        $[39] = t15;
        $[40] = t7;
        $[41] = t16;
      } else {
        t16 = $[41];
      }
      content = t16;
    }
  }
  let t3;
  if ($[42] !== content || $[43] !== isOpen || $[44] !== onClose) {
    t3 = <SimpleModal isOpen={isOpen} onClose={onClose}>{content}</SimpleModal>;
    $[42] = content;
    $[43] = isOpen;
    $[44] = onClose;
    $[45] = t3;
  } else {
    t3 = $[45];
  }
  return t3;
}
function _temp(data) {
  return data.partnerProfile;
}